import browser from "web/script/utils/browser";
import logging from "web/script/utils/logging";

/**
 * Handler for scroll events, if scroll position is close enough to the bottom of the
 * page then more content is loaded by calling the `loadMoreContent` promise – which
 * will need to be implemented on the component using this mixin.
 */
function onScroll() {
    if (this._infiniteScrollIgnore || this._infiniteScrollLocked) {
        return;
    }

    const triggerDistance = this.options.infiniteScrollTriggerDistance || 2000;

    if (isPastTriggerPoint(triggerDistance)) {
        this._infiniteScrollLocked = true;

        this.loadMoreContent(this._infiniteScrollNextURL)
            .then((nextURL) => {
                this._infiniteScrollLocked = false;
                this._infiniteScrollNextURL = nextURL;
                this._infiniteScrollEnded = !nextURL;

                if (this._infiniteScrollEnded) {
                    this.releaseGlobalHandler("scroll", onScroll);
                    this.emit("infinite-scroll-end");
                }
            })
            .catch((error) => {
                logging.error(error);
                return null;
            });
    }
}

/**
 * Handler for scroll to top starting. Only applicable if the component is using the
 * `scroll-to-top-button` component.
 */
function onScrollToTopStart() {
    this._infiniteScrollIgnore = true;
}

/**
 * Handler for scroll to top ending. Only applicable if the component is using the
 * `scroll-to-top-button` component.
 */
function onScrollToTopEnd() {
    this._infiniteScrollIgnore = false;
}

/**
 * Helper for creating a function that will only be called if infinite scroll is not disabled.
 * @param {Function} fn
 * @returns {Function}
 */
function onlyIfEnabled(fn) {
    return function(...args) {
        if (this.options.infiniteScrollEnabled !== false) {
            return fn.apply(this, args);
        }
    };
}

/**
 * Returns true if the user has scrolled past the trigger point for the infinite scroll,
 * false if not.
 *
 * @params {Integer} triggerDistance
 * @returns {Boolean}
 */
function isPastTriggerPoint(triggerDistance) {
    const documentHeight = browser.getDocumentHeight();
    const viewportHeight = browser.getWindowHeight();
    const scrollTop = browser.scrollTop();

    const scrolledDistance = scrollTop + viewportHeight;
    const triggerPoint = documentHeight - (viewportHeight + triggerDistance);

    return scrolledDistance > triggerPoint;
}

/**
 * Infinite scroll mixin.
 */
export default {
    defaultOptions: {
        enableInfiniteScroll: true,
    },

    setupEvents: onlyIfEnabled(function(add) {
        add("scroll-to-top-start", onScrollToTopStart);
        add("scroll-to-top-end", onScrollToTopEnd);
    }),

    init: onlyIfEnabled(function() {
        this._infiniteScrollLocked = false;
        this._infiniteScrollIgnore = !this.options.enableInfiniteScroll;
        this._infiniteScrollEnded = false;
    }),

    onInsert: onlyIfEnabled(function() {
        const paginationContainer = this.el.querySelector("[data-pagination-container]");
        const nextPageLink = this.el.querySelector("[data-next-link]");
        const url = nextPageLink
            ? nextPageLink.getAttribute("data-next-link")
            : this._infiniteScrollNextURL;

        if (!url) {
            this._infiniteScrollEnded = true;
            this.emit("infinite-scroll-end");
            return;
        }

        if (paginationContainer) {
            paginationContainer.parentElement.removeChild(paginationContainer);
        }

        if (nextPageLink) {
            nextPageLink.parentElement.removeChild(nextPageLink);
        }

        this._infiniteScrollNextURL = url;

        this.setGlobalHandler("scroll", onScroll);
        onScroll.call(this);
    }),

    onRemove: onlyIfEnabled(function() {
        this.releaseGlobalHandler("scroll", onScroll);
    }),

    disableInfiniteScroll() {
        this._infiniteScrollIgnore = true;
    },

    enableInfiniteScroll() {
        this._infiniteScrollIgnore = false;
        onScroll.call(this);
    },
};
